<template>
  <div class="bg-white rounded-lg shadow-md p-6">
    <div class="mb-6">
      <h3 class="text-xl font-medium mb-2">Due Date Management</h3>
      <p class="text-gray-500 mb-4">
        Configure due dates for different actions and document types in the system.
      </p>
    </div>

    <!-- Due Date Configuration Table -->
    <div>
      <!-- Filter dropdown -->
      <div class="mb-2 flex justify-end items-center">
        <label for="forecast-filter" class="mr-2 text-sm font-medium text-gray-700">Filter by Forecasting Process:</label>
        <div class="w-64">
          <select
            id="forecast-filter"
            v-model="selectedForecastFilter"
            class="block w-full pl-3 pr-10 py-1 text-sm border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 rounded-md"
          >
            <option value="">All Forecasting Processes</option>
            <option v-for="process in forecastingProcesses" :key="process.id" :value="process.id">
              {{ process.name }}
            </option>
          </select>
        </div>
      </div>
      
      <div ref="tableContainer" class="overflow-auto" style="max-height: 300px; border: 1px solid #e5e7eb; border-radius: 0.375rem;">
        <table class="min-w-full divide-y divide-gray-200">
        <thead class="bg-gray-50 sticky top-0 z-10">
          <tr>
            <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Forecasting Process
              <span class="text-xs font-normal text-gray-400 ml-2 lowercase">
                ({{ filteredDueDates.length }} of {{ dueDates.length }} entries)
              </span>
            </th>
            <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Action</th>
            <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Document Type</th>
            <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Absolute Date</th>
            <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Relative Hours</th>
            <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Consider Workdays</th>
            <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
          </tr>
        </thead>
        <tbody class="bg-white divide-y divide-gray-200">
          <tr v-if="loading">
            <td colspan="7" class="px-6 py-4 text-center text-gray-500">
              Loading due date configurations...
            </td>
          </tr>
          <tr v-else-if="filteredDueDates.length === 0">
            <td colspan="7" class="px-6 py-4 text-center text-gray-500">
              No due date configurations found. Add one below.
            </td>
          </tr>
          <tr v-for="(dueDate, index) in filteredDueDates" :key="index" class="hover:bg-gray-50 transition-colors duration-300" :data-id="dueDate.id">
            <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
              {{ getForecastName(dueDate.forecast_id) || 'Not assigned' }}
            </td>
            <td class="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
              {{ dueDate.action }}
            </td>
            <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
              {{ dueDate.document_type || 'All Types' }}
            </td>
            <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
              {{ dueDate.absolute ? formatDateTime(dueDate.absolute) : 'Not set' }}
            </td>
            <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
              {{ dueDate.relative_hours }} hours
            </td>
            <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
              <span v-if="dueDate.consider_workdays" class="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">
                Yes
              </span>
              <span v-else class="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-gray-100 text-gray-800">
                No
              </span>
            </td>
            <td class="px-6 py-4 whitespace-nowrap text-sm font-medium">
              <button @click="editDueDate(dueDate)" class="text-indigo-600 hover:text-indigo-900 mr-3">Edit</button>
              <button @click="deleteDueDate(dueDate.id)" class="text-red-600 hover:text-red-900">Delete</button>
            </td>
          </tr>
        </tbody>
        </table>
      </div>
    </div>

    <!-- Add/Edit Due Date Form -->
    <div class="mt-8 bg-gray-50 p-6 rounded-md">
      <h4 class="text-lg font-medium mb-4">{{ isEditing ? 'Edit' : 'Add' }} Due Date Configuration</h4>
      <form @submit.prevent="saveDueDate">
        <!-- First row: Forecasting Process, Action, Document Type -->
        <div class="mb-6">
          <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
            <!-- Forecasting Process Field -->
            <div>
              <label for="forecast_id" class="block text-sm font-medium text-gray-700">
                Forecasting Process <span class="text-red-500">*</span>
              </label>
              <div v-if="forecastingProcesses.length === 0" class="mt-1 text-sm text-red-500">
                No forecasting processes available. Please create a forecasting process first.
              </div>
              <select
                v-else
                id="forecast_id"
                v-model="formData.forecast_id"
                class="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md"
                required
                :class="{'border-red-300 focus:ring-red-500 focus:border-red-500': !formData.forecast_id && formTouched}"
              >
                <option value="">Select a forecasting process</option>
                <option v-for="process in forecastingProcesses" :key="process.id" :value="process.id">
                  {{ process.name }} ({{ formatDate(process.start_date) }})
                </option>
              </select>
              <p v-if="!formData.forecast_id && formTouched && forecastingProcesses.length > 0" class="mt-1 text-sm text-red-500">
                Forecasting Process is required
              </p>
            </div>
            
            <!-- Action Field -->
            <div>
              <label for="action" class="block text-sm font-medium text-gray-700">
                Action <span class="text-red-500">*</span>
              </label>
              <select
                id="action"
                v-model="formData.action"
                class="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md"
                required
                :class="{'border-red-300 focus:ring-red-500 focus:border-red-500': !formData.action && formTouched}"
              >
                <option value="">Select an action</option>
                <option v-for="action in actionOptions" :key="action" :value="action">{{ action }}</option>
              </select>
              <p v-if="!formData.action && formTouched" class="mt-1 text-sm text-red-500">
                Action is required
              </p>
            </div>
            
            <!-- Document Type Field -->
            <div>
              <label for="document_type" class="block text-sm font-medium text-gray-700">Document Type</label>
              <select
                id="document_type"
                v-model="formData.document_type"
                class="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md"
              >
                <option value="">All Types</option>
                <option v-for="type in documentTypeOptions" :key="type" :value="type">{{ type }}</option>
              </select>
            </div>
          </div>
        </div>
        
        <!-- Second row: Absolute Date, Relative Hours, Consider Workdays -->
        <div class="mb-4">
          <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
            <!-- Absolute Date Field -->
            <div>
              <label for="absolute_date" class="block text-sm font-medium text-gray-700">Absolute Date</label>
              <div class="mt-1">
                <input
                  type="datetime-local"
                  id="absolute_date"
                  v-model="formData.absolute"
                  class="focus:ring-indigo-500 focus:border-indigo-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                />
                <p class="text-xs text-gray-500 mt-1">
                  If set, this exact date will be used instead of calculating from relative hours.
                </p>
              </div>
            </div>
            
            <!-- Relative Hours Field -->
            <div>
              <label for="relative_hours" class="block text-sm font-medium text-gray-700">
                Relative Hours
              </label>
              <input
                type="number"
                id="relative_hours"
                v-model.number="formData.relative_hours"
                min="1"
                class="mt-1 focus:ring-indigo-500 focus:border-indigo-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                :disabled="!!formData.absolute && formData.absolute.trim() !== ''"
                :class="{'border-red-300 focus:ring-red-500 focus:border-red-500': !formData.relative_hours && !formData.absolute && formTouched}"
              />
              <p v-if="!!formData.absolute && formData.absolute.trim() !== ''" class="text-xs text-gray-500 mt-1">
                Disabled when absolute date is set.
              </p>
              <p v-else-if="!formData.relative_hours && !formData.absolute && formTouched" class="mt-1 text-sm text-red-500">
                Either Relative Hours or Absolute Date is required
              </p>
            </div>
            
            <!-- Consider Workdays Field -->
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">Consider Workdays</label>
              <div class="mt-2 flex items-center">
                <input
                  id="consider_workdays"
                  type="checkbox"
                  v-model="formData.consider_workdays"
                  class="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                />
                <label for="consider_workdays" class="ml-2 block text-sm text-gray-700">
                  Skip Weekends
                </label>
              </div>
            </div>
          </div>
        </div>
        
        <div class="mt-6 flex justify-between items-center">
          <p class="text-sm text-gray-500">Fields marked with <span class="text-red-500">*</span> are required</p>
          
          <div class="flex space-x-3">
            <button
              type="button"
              @click="resetForm"
              class="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            >
              Cancel
            </button>
            <button
              type="submit"
              class="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
              :disabled="forecastingProcesses.length === 0 || !isFormValid"
              :title="!isFormValid ? 'Please fill in all required fields' : ''"
            >
              {{ isEditing ? 'Update' : 'Add' }} Due Date
            </button>
          </div>
        </div>
      </form>
    </div>

    <div class="mt-8 flex justify-center">
      <router-link
        to="/administration"
        class="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-gray-600 hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 inline-block"
      >
        Return to Administration
      </router-link>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, onMounted, watch, computed } from 'vue';
import { supabase } from '@/services/supabase';
import { logger, LogCategory } from '@/services/logger';
import { ACTIONS } from '@/types/taskActions';
import { REPORT_TYPE } from '@/types/document';
import { IDueDates } from '@/types/dueDates';

export default defineComponent({
  name: 'DueDateManagement',
  emits: ['navigate'],
  setup() {
    const dueDates = ref<IDueDates[]>([]);
    const loading = ref(true);
    const isEditing = ref(false);
    const currentEditId = ref<string | null>(null);
    const tableContainer = ref<HTMLElement | null>(null);
    const lastAddedId = ref<string | null>(null);
    const selectedForecastFilter = ref('');
    
    const formData = ref({
      action: '',
      document_type: '',
      relative_hours: 48 as number | null,
      consider_workdays: true,
      absolute: '',
      forecast_id: ''
    });
    
    // Store forecasting processes
    const forecastingProcesses = ref<any[]>([]);
    
    // Action options from the ACTIONS enum
    const actionOptions = Object.values(ACTIONS);
    
    // Document type options from the REPORT_TYPE enum
    const documentTypeOptions = Object.values(REPORT_TYPE);
    
    // Fetch forecasting processes
    const fetchForecastingProcesses = async () => {
      try {
        // First, check if the table exists by getting a single row
        // const { count, error: countError } = await supabase
        //   .from('forecasting_process')
        //   .select('*', { count: 'exact', head: true });
          
        // if (countError) {
        //   console.error('Error checking forecasting_process table:', countError);
        //   throw countError;
        // }
        
        // console.log('Forecasting process count:', count);
        
        // Now fetch all rows
        const { data, error } = await supabase
          .from('forecasting_process')
          .select('id, name, start_date, status')
          .order('start_date', { ascending: false });
          
        if (error) {
          console.error('Error fetching forecasting processes:', error);
          throw error;
        }
        
        console.log('Raw forecasting processes data:', data);
        
        // Check if data is valid
        if (!data || !Array.isArray(data)) {
          forecastingProcesses.value = [];
        } else {
          forecastingProcesses.value = data;
        }
        
        if (forecastingProcesses.value.length === 0) {
          // If no forecasting processes found, log a warning
          logger.warn('No forecasting processes found', LogCategory.DATABASE);
        }
      } catch (error) {
        logger.error('Error fetching forecasting processes', LogCategory.DATABASE, { error });
        forecastingProcesses.value = [];
      }
    };
    
    // Fetch all due dates from the database
    const fetchDueDates = async () => {
      loading.value = true;
      try {
        const { data, error } = await supabase
          .from('due_dates')
          .select('*')
          .order('action');
          
        if (error) {
          throw error;
        }
        
        dueDates.value = data || [];
      } catch (error) {
        logger.error('Error fetching due dates', LogCategory.DATABASE, { error });
      } finally {
        loading.value = false;
      }
    };
    
    // Save a new due date or update an existing one
    const saveDueDate = async () => {
      try {
        // Check if there are any forecasting processes
        if (forecastingProcesses.value.length === 0) {
          alert('No forecasting processes available. Please create a forecasting process first.');
          return;
        }
        
        // Validate required fields
        if (!formData.value.forecast_id) {
          alert('Please select a forecasting process');
          return;
        }
        
        // Prepare payload
        const payload = {
          action: formData.value.action,
          document_type: formData.value.document_type || null,
          relative_hours: formData.value.relative_hours,
          consider_workdays: formData.value.consider_workdays,
          absolute: formData.value.absolute || null,
          forecast_id: formData.value.forecast_id
        };
        
        // If absolute date is set, ensure relative_hours is null
        if (payload.absolute) {
          payload.relative_hours = null;
        } else if (payload.relative_hours === null) {
          // If no absolute date but relative_hours is null, set a default
          payload.relative_hours = 48;
        }
        
        if (isEditing.value && currentEditId.value) {
          // Update existing due date
          const { error } = await supabase
            .from('due_dates')
            .update(payload)
            .eq('id', currentEditId.value);
            
          if (error) throw error;
          
          logger.info('Due date updated successfully', LogCategory.DATABASE, {
            id: currentEditId.value,
            ...payload
          });
          
          lastAddedId.value = currentEditId.value;
        } else {
          // Create new due date
          const { data, error } = await supabase
            .from('due_dates')
            .insert([payload])
            .select();
            
          if (error) throw error;
          
          if (data && data.length > 0) {
            lastAddedId.value = data[0].id;
          }
          
          logger.info('Due date created successfully', LogCategory.DATABASE, payload);
        }
        
        // Reset form and refresh data
        resetForm();
        await fetchDueDates();
        
        // Scroll to the newly added item after a short delay to ensure the DOM has updated
        setTimeout(() => {
          // Clear any filter to ensure the newly added item is visible
          selectedForecastFilter.value = '';
          scrollToLastAddedItem();
        }, 100);
      } catch (error) {
        logger.error('Error saving due date', LogCategory.DATABASE, { error });
      }
    };
    
    // Edit an existing due date
    const editDueDate = (dueDate: IDueDates) => {
      isEditing.value = true;
      currentEditId.value = dueDate.id;
      formTouched.value = true; // Mark form as touched when editing
      
      // Check if the forecast_id exists in the forecastingProcesses array
      const forecastExists = forecastingProcesses.value.some(p => p.id === dueDate.forecast_id);
      
      if (!forecastExists && dueDate.forecast_id) {
        // Re-fetch forecasting processes to ensure we have the latest data
        fetchForecastingProcesses();
      }
      
      formData.value = {
        action: dueDate.action,
        document_type: dueDate.document_type || '',
        relative_hours: dueDate.absolute ? null : (dueDate.relative_hours || 48),
        consider_workdays: dueDate.consider_workdays,
        absolute: dueDate.absolute || '',
        forecast_id: dueDate.forecast_id || ''
      };
    };
    
    // Delete a due date
    const deleteDueDate = async (id: string) => {
      if (!confirm('Are you sure you want to delete this due date configuration?')) {
        return;
      }
      
      try {
        const { error } = await supabase
          .from('due_dates')
          .delete()
          .eq('id', id);
          
        if (error) throw error;
        
        logger.info('Due date deleted successfully', LogCategory.DATABASE, { id });
        fetchDueDates();
      } catch (error) {
        logger.error('Error deleting due date', LogCategory.DATABASE, { error });
      }
    };
    
    // Reset the form
    const resetForm = () => {
      isEditing.value = false;
      currentEditId.value = null;
      formTouched.value = false;
      formData.value = {
        action: '',
        document_type: '',
        relative_hours: 48,
        consider_workdays: true,
        absolute: '',
        forecast_id: ''
      };
    };
    
    // Get forecast name by ID
    const getForecastName = (forecastId: string) => {
      if (!forecastId) return null;
      
      // Check if forecastingProcesses is populated
      if (forecastingProcesses.value.length === 0) {
        // Try to fetch forecasting processes if they're not loaded yet
        fetchForecastingProcesses();
        return `Loading... (ID: ${forecastId.substring(0, 8)}...)`;
      }
      
      const forecast = forecastingProcesses.value.find(p => p.id === forecastId);
      
      if (!forecast) {
        return `Unknown (ID: ${forecastId.substring(0, 8)}...)`;
      }
      
      return forecast.name;
    };
    
    // Format date for display (without time)
    const formatDate = (dateString: string) => {
      if (!dateString) return '';
      
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    };
    
    // Track if form has been touched
    const formTouched = ref(false);
    
    // Watch for any changes to formData to mark form as touched
    watch(() => formData.value, () => {
      formTouched.value = true;
    }, { deep: true });
    
    // Filtered due dates based on selected forecast process
    const filteredDueDates = computed(() => {
      if (!selectedForecastFilter.value) {
        return dueDates.value;
      }
      
      return dueDates.value.filter(dueDate =>
        dueDate.forecast_id === selectedForecastFilter.value
      );
    });
    
    // Computed property to check if form is valid
    const isFormValid = computed(() => {
      // If form hasn't been touched yet, consider it invalid
      if (!formTouched.value) {
        return false;
      }
      
      // Check if action is selected
      if (!formData.value.action) {
        return false;
      }
      
      // Check if forecast_id is selected
      if (!formData.value.forecast_id) {
        return false;
      }
      
      // Check if either relative_hours or absolute date is set
      // At least one of them must be provided
      if (!formData.value.absolute &&
          (formData.value.relative_hours === null || formData.value.relative_hours <= 0)) {
        return false;
      }
      
      return true;
    });
    
    // Watch for changes to the absolute date field
    watch(() => formData.value.absolute, (newValue) => {
      // If an absolute date is set, clear the relative hours
      if (newValue && newValue.trim() !== '') {
        console.log('Absolute date set, clearing relative hours');
        formData.value.relative_hours = null;
      } else if (formData.value.relative_hours === null) {
        // If absolute date is cleared and relative_hours is null, reset to default
        formData.value.relative_hours = 48;
      }
    });
    
    // Fetch due dates and forecasting processes on component mount
    onMounted(() => {
      // Fetch due dates
      fetchDueDates();
    
      // Fetch forecasting processes
      fetchForecastingProcesses();
    });
    
    // Format date for display
    const formatDateTime = (dateString: string) => {
      if (!dateString) return '';
      
      const date = new Date(dateString);
      return date.toLocaleString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    };
    
    // Scroll to the last added item
    const scrollToLastAddedItem = () => {
      if (!lastAddedId.value || !tableContainer.value) return;
      
      // Find the newly added row
      const rows = tableContainer.value.querySelectorAll('tbody tr');
      let targetRow: Element | null = null;
      
      for (let i = 0; i < rows.length; i++) {
        const row = rows[i];
        const rowId = row.getAttribute('data-id');
        if (rowId === lastAddedId.value) {
          targetRow = row;
          break;
        }
      }
      
      if (targetRow) {
        // Highlight the row briefly
        targetRow.classList.add('bg-green-50');
        setTimeout(() => {
          if (targetRow) {
            targetRow.classList.remove('bg-green-50');
          }
        }, 2000);
        
        // Scroll to the row
        targetRow.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    };
    
    return {
      dueDates,
      filteredDueDates,
      loading,
      isEditing,
      formData,
      actionOptions,
      documentTypeOptions,
      saveDueDate,
      editDueDate,
      deleteDueDate,
      resetForm,
      formatDateTime,
      forecastingProcesses,
      getForecastName,
      formatDate,
      isFormValid,
      formTouched,
      tableContainer,
      selectedForecastFilter
    };
  }
});
</script>

<style scoped>
.bg-green-50 {
  background-color: rgba(240, 253, 244, 1);
  transition: background-color 0.5s ease-in-out;
}
</style>